import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import PageHeader from '../../ui-components/PageHeader';
import {
  API, ACCENT, Cel, NAV_FONT, NORM, ROOD, bovenPct, haal, kleurVoor, maandLabel, pct, uren,
} from './Declarabiliteit';

/**
 * Declarabiliteit per persoon (Joris, 23 sep): waar gingen de uren dit jaar heen — per soort werk,
 * intern of extern, binnen of boven budget — en hoe verhoudt dat zich tot collega's met dezelfde
 * functie. Bron: /Api/declarabiliteit/persoon/{email}/jaar (zelfde rekenregels als de matrix).
 */
const KLEUR = { binnen: '#16a34a', boven: ROOD, overig: '#cbd5e1', intern: '#818cf8' };

function Tegel({ label, waarde, sub, kleur }) {
  return (
    <Box sx={{ px: 2, py: 1.25, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: kleur?.bg || '#fff', minWidth: 150, flex: '1 1 150px' }}>
      <Typography sx={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
      <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 22, color: kleur?.fg || '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{waarde}</Typography>
      {sub && <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{sub}</Typography>}
    </Box>
  );
}

/** Eén soort werk als liggende balk: binnen/boven/overig (extern) of intern. */
function SoortBalk({ s, max }) {
  const breedte = max > 0 ? Math.max(2, (100 * s.geschreven) / max) : 0;
  const delen = s.groep === 'intern'
    ? [['intern', s.geschreven]]
    : [['binnen', s.binnen], ['boven', s.boven], ['overig', s.overig]];
  const uitleg = s.groep === 'intern'
    ? `Intern · ${uren(s.geschreven)}`
    : `Binnen budget ${uren(s.binnen)} · boven budget ${uren(s.boven)}${s.overig ? ` · kostenregel/offerte/budget onbekend ${uren(s.overig)}` : ''}`;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 260px) 1fr 150px' }, gap: { xs: 0.5, md: 1.5 }, alignItems: 'center', py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontWeight: 700, fontSize: 13.5 }}>{s.soort}</Typography>
        <Typography sx={{ fontSize: 11, color: s.groep === 'intern' ? KLEUR.intern : '#64748b', fontWeight: 600 }}>
          {s.groep === 'intern' ? 'intern' : 'extern'}
        </Typography>
      </Box>
      <Tooltip title={uitleg} arrow>
        <Box sx={{ height: 14, width: `${breedte}%`, display: 'flex', borderRadius: 1, overflow: 'hidden', bgcolor: '#f1f5f9' }}>
          {delen.filter(([, u]) => u > 0).map(([k, u]) => (
            <Box key={k} sx={{ width: `${(100 * u) / (s.geschreven || 1)}%`, bgcolor: KLEUR[k] }} />
          ))}
        </Box>
      </Tooltip>
      <Box sx={{ textAlign: { md: 'right' }, fontVariantNumeric: 'tabular-nums' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
          {uren(s.geschreven)} <Box component="span" sx={{ color: '#64748b', fontWeight: 500 }}>· {pct(s.aandeel)}</Box>
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
          functie gem. {pct(s.aandeel_groep)}{s.boven >= 0.5 ? ' · ' : ''}
          {s.boven >= 0.5 && <Box component="span" sx={{ color: ROOD, fontWeight: 700 }}>▲ {uren(s.boven)} boven</Box>}
        </Typography>
      </Box>
    </Box>
  );
}

/** Per persoon een verse component (key = e-mail): een functiekeuze voor de ene collega mag niet
 *  meeverhuizen naar de volgende (Codex-review 23 sep). */
export default function DeclarabiliteitPersoon() {
  const { email } = useParams();
  return <PersoonInhoud key={email} email={email} />;
}

function PersoonInhoud({ email }) {
  const ditJaar = new Date().getFullYear();
  const [jaar, setJaar] = useState(ditJaar);
  const [functie, setFunctie] = useState('');
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);

  useEffect(() => {
    let actief = true;
    setData(null);
    setFout(null);
    const q = new URLSearchParams({ jaar: String(jaar) });
    if (functie) q.set('functie', functie);
    haal(`${API}/persoon/${encodeURIComponent(email)}/jaar?${q}`)
      .then((d) => { if (actief) setData(d); })
      .catch((e) => { if (actief) setFout(e.message); });
    return () => { actief = false; };
  }, [email, jaar, functie]);

  const t = data?.totaal;
  const g = data?.vergelijking?.groep;
  const soorten = data?.soorten || [];
  const max = Math.max(0, ...soorten.map((s) => s.geschreven || 0));
  const functies = data?.persoon?.functies || [];

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <Box component={RouterLink} to="/declarabiliteit" sx={{ display: 'inline-block', mb: 1, fontSize: 13, color: ACCENT.color, fontWeight: 700, textDecoration: 'none' }}>
        ← Declarabiliteit
      </Box>
      <PageHeader
        icon="person"
        title={data?.persoon?.naam || 'Declarabiliteit per persoon'}
        subtitle={`${data?.persoon?.functie || ''}${data?.persoon?.functie ? ' · ' : ''}${jaar}${jaar === ditJaar ? ' t/m gisteren' : ''}`}
        accent={ACCENT}
        rightActions={(
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField select size="small" label="Jaar" value={jaar} onChange={(e) => setJaar(Number(e.target.value))} sx={{ width: 110 }}>
              {[ditJaar, ditJaar - 1, ditJaar - 2].map((j) => <MenuItem key={j} value={j}>{j}</MenuItem>)}
            </TextField>
            {(functies.length > 0 || data?.persoon?.groep) && (
              <TextField select size="small" label="Vergelijk met" value={functie || data?.vergelijking?.functie || ''}
                onChange={(e) => setFunctie(e.target.value)} sx={{ minWidth: 210 }}>
                {data?.persoon?.groep && (
                  <MenuItem value={`groep:${data.persoon.groep}`}>Functiegroep {data.persoon.groep}</MenuItem>
                )}
                {functies.map((f) => <MenuItem key={f} value={f}>Functie {f}</MenuItem>)}
              </TextField>
            )}
          </Box>
        )}
      />

      {fout && <Alert severity="warning" sx={{ mb: 2 }}>{fout}</Alert>}
      {!data && !fout && <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress size={24} /></Box>}

      {data && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
            <Tegel label="Declarabiliteit" waarde={`${t.ondergrens && t.declarabiliteit !== null ? '≥' : ''}${pct(t.declarabiliteit)}`}
              sub={`functie gem. ${pct(g?.declarabiliteit)} · norm ${NORM}%`} kleur={kleurVoor(t.declarabiliteit)} />
            <Tegel label="Geschreven" waarde={uren(t.geschreven)} sub={`beschikbaar ${uren(t.beschikbaar)}`} />
            <Tegel label="Intern" waarde={pct(t.intern_pct)} sub={`${uren(t.intern)} · functie gem. ${pct(g?.intern_pct)}`} />
            <Tegel label="Extern binnen budget" waarde={pct(t.binnen_pct_van_extern)} sub={`${uren(t.binnen)} van ${uren(t.extern)} extern`} />
            <Tegel label="Boven budget" waarde={uren(t.boven)} kleur={(t.boven || 0) > 0 ? { bg: '#fef2f2', fg: ROOD } : null}
              sub={`${pct(bovenPct(t))} van extern · functie gem. ${pct(bovenPct(g))}`} />
            <Tegel label="Overig niet declarabel" waarde={uren(t.overig_niet)} sub="offertefase en kostenregels" />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 3, alignItems: 'start' }}>
            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2.5, bgcolor: '#fff' }}>
              <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 16, mb: 0.5 }}>Waar gingen de uren heen</Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1.5 }}>
                Per soort werk. Groen = binnen budget, rood = boven budget, grijs = kostenregel, offertefase of onbekend budget, paars = intern.
                "Functie gem." = aandeel van dit soort werk bij de vergelijkingsgroep ({(data.vergelijking.functie || '—').replace(/^groep:/, 'functiegroep ')}) samen.
              </Typography>
              {soorten.length === 0 && <Typography color="text.secondary">Geen uren in {jaar}.</Typography>}
              {soorten.map((s) => <SoortBalk key={`${s.groep}|${s.soort}`} s={s} max={max} />)}
            </Box>

            <Box sx={{ display: 'grid', gap: 3 }}>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2.5, bgcolor: '#fff' }}>
                <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 16, mb: 1.5 }}>Per maand</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: 1 }}>
                  {data.maanden.map((c) => (
                    <Box key={c.maand}>
                      <Typography sx={{ fontSize: 11, color: '#64748b', textAlign: 'center', mb: 0.25 }}>{maandLabel(c.maand)}</Typography>
                      <Cel c={c} />
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2.5, bgcolor: '#fff', overflowX: 'auto' }}>
                <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 16, mb: 0.5 }}>
                  Vergelijking: {(data.vergelijking.functie || 'dezelfde functie').replace(/^groep:/, 'functiegroep ')}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1.5 }}>
                  {g?.aantal || 0} {g?.aantal === 1 ? 'persoon' : 'personen'} met deze functie in {jaar}, gesorteerd op declarabiliteit.
                </Typography>
                <Box component="table" sx={{
                  width: '100%', borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums',
                  '& th': { textAlign: 'right', fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', py: 0.75, px: 0.75, whiteSpace: 'nowrap' },
                  '& th:first-of-type, & td:first-of-type': { textAlign: 'left' },
                  '& td': { textAlign: 'right', borderBottom: '1px solid #f1f5f9', py: 0.75, px: 0.75 },
                }}>
                  <thead>
                    <tr><th>Naam</th><th>Declarabel</th><th>Intern</th><th>Binnen budget</th><th>Boven budget</th><th>Geschreven</th></tr>
                  </thead>
                  <tbody>
                    {data.vergelijking.personen.map((r) => (
                      <Box component="tr" key={r.email} sx={{ bgcolor: r.zelf ? '#eef2ff' : undefined, fontWeight: r.zelf ? 700 : 400 }}>
                        <td>
                          {r.zelf ? r.naam : (
                            <Box component={RouterLink} to={`/declarabiliteit/persoon/${encodeURIComponent(r.email)}`}
                              sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: ACCENT.color, textDecoration: 'underline' } }}>
                              {r.naam}
                            </Box>
                          )}
                        </td>
                        <td style={{ color: kleurVoor(r.declarabiliteit).fg, fontWeight: 700 }}>{r.ondergrens && r.declarabiliteit !== null ? '≥' : ''}{pct(r.declarabiliteit)}</td>
                        <td>{pct(r.intern_pct)}</td>
                        <td>{pct(r.binnen_pct_van_extern)}</td>
                        <td style={{ color: (r.boven || 0) >= 0.5 ? ROOD : undefined }}>{uren(r.boven)} · {pct(r.boven_pct_van_extern)}</td>
                        <td>{uren(r.geschreven)}</td>
                      </Box>
                    ))}
                    {g && (
                      <tr style={{ borderTop: '2px solid #e2e8f0' }}>
                        <td><em>Functie totaal</em></td>
                        <td>{pct(g.declarabiliteit)}</td>
                        <td>{pct(g.intern_pct)}</td>
                        <td>{pct(g.binnen_pct_van_extern)}</td>
                        <td>{uren(g.boven)} · {pct(g.boven_pct_van_extern)}</td>
                        <td>{uren(g.geschreven)}</td>
                      </tr>
                    )}
                  </tbody>
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
